import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { requestHelpers } from '../../services/api';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, hasFeature, refreshProfile } = useAuth();
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeContacts: 0,
    messagesSent: 0,
    responseRate: 0,
    activeFlows: 0,
    deliveryRate: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await requestHelpers.get('/analytics/dashboard');
        const { overview, recentActivity: activities } = response.data;
        setStats({
          messagesSent: overview.messages?.outboundMessages || 0,
          deliveryRate: overview.messages?.deliveryRate || 0,
          readRate: overview.messages?.readRate || 0,
          activeFlows: overview.flows?.activeFlows || 0,
          totalContacts: overview.contacts?.totalContacts || 0,
          activeContacts: overview.contacts?.activeContacts || 0,
          totalCampaigns: overview.campaigns?.totalCampaigns || 0
        });
        if (activities) {
          setRecentActivity(activities);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const checkMediaStatus = async () => {
      try {
        const response = await requestHelpers.get('/user/media-check');
        if (response.data.expiredCount > 0 || response.data.expiringSoonCount > 0) {
          const { expiredCount, expiringSoonCount } = response.data;
          if (expiredCount > 0) {
            toast.warning(`⚠️ ${expiredCount} media file(s) have expired. Please re-upload them in your flows/chat.`, {
              autoClose: false,
              closeOnClick: true
            });
          } else if (expiringSoonCount > 0) {
            toast.info(`ℹ️ ${expiringSoonCount} media file(s) will expire soon.`, {
              autoClose: 10000
            });
          }
        }
      } catch (err) {
        console.error('Media check failed:', err);
      }
    };

    fetchStats();
    checkMediaStatus();
    // Refresh user profile to get latest wallet balance
    refreshProfile().catch(() => {});
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'campaign': return 'fas fa-bullhorn';
      case 'contact': return 'fas fa-user-plus';
      case 'transaction': return 'fas fa-wallet';
      case 'template': return 'fas fa-file-alt';
      default: return 'fas fa-info-circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'success': return '#17a2b8';
      case 'info': return '#6c757d';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dashboard-loading">
          <LoadingSpinner size="large" message="Loading your dashboard..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dashboard-content">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>Welcome back, {user?.name}! 👋</h1>
            <p>Here's what's happening with your MessagePe campaigns today.</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon campaigns">
              <i className="fas fa-bullhorn"></i>
            </div>
            <div className="stat-content">
              <h3>{stats.totalCampaigns || 0}</h3>
              <p>Total Campaigns</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon contacts">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <h3>{(stats.totalContacts || 0).toLocaleString()}</h3>
              <p>Total Contacts</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon messages">
              <i className="fas fa-paper-plane"></i>
            </div>
            <div className="stat-content">
              <h3>{(stats.messagesSent || 0).toLocaleString()}</h3>
              <p>Messages Sent</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon response">
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="stat-content">
              <h3>{(stats.deliveryRate || 0).toFixed(1)}%</h3>
              <p>Delivery Rate</p>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Feature Access Cards */}
        <div className="feature-grid">
          <div className={`feature-card ${hasFeature('campaigns') ? 'enabled' : 'disabled'}`}>
            <div className="feature-icon">
              <i className="fas fa-bullhorn"></i>
            </div>
            <h3>Campaigns</h3>
            <p>Create and manage WhatsApp marketing campaigns</p>
            {hasFeature('campaigns') ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/campaigns')}>
                Create Campaign
              </button>
            ) : (
              <span className="feature-disabled">Not Available</span>
            )}
          </div>

          <div className={`feature-card ${hasFeature('contacts') ? 'enabled' : 'disabled'}`}>
            <div className="feature-icon">
              <i className="fas fa-address-book"></i>
            </div>
            <h3>Contacts</h3>
            <p>Manage your contact lists and segments</p>
            {hasFeature('contacts') ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/contacts')}>
                Manage Contacts
              </button>
            ) : (
              <span className="feature-disabled">Not Available</span>
            )}
          </div>

          <div className={`feature-card ${hasFeature('templates') ? 'enabled' : 'disabled'}`}>
            <div className="feature-icon">
              <i className="fas fa-file-alt"></i>
            </div>
            <h3>Templates</h3>
            <p>Create reusable message templates</p>
            {hasFeature('templates') ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/templates')}>
                Create Template
              </button>
            ) : (
              <span className="feature-disabled">Not Available</span>
            )}
          </div>

          <div className={`feature-card ${hasFeature('analytics') ? 'enabled' : 'disabled'}`}>
            <div className="feature-icon">
              <i className="fas fa-chart-bar"></i>
            </div>
            <h3>Analytics</h3>
            <p>Track performance and engagement metrics</p>
            {hasFeature('analytics') ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/analytics')}>
                View Analytics
              </button>
            ) : (
              <span className="feature-disabled">Not Available</span>
            )}
          </div>

          <div className={`feature-card ${hasFeature('automation') ? 'enabled' : 'disabled'}`}>
            <div className="feature-icon">
              <i className="fas fa-robot"></i>
            </div>
            <h3>Automation</h3>
            <p>Set up automated message workflows</p>
            {hasFeature('automation') ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/automation')}>
                Setup Automation
              </button>
            ) : (
              <span className="feature-disabled">Not Available</span>
            )}
          </div>

          <div className={`feature-card ${hasFeature('reports') ? 'enabled' : 'disabled'}`}>
            <div className="feature-icon">
              <i className="fas fa-file-pdf"></i>
            </div>
            <h3>Reports</h3>
            <p>Generate detailed performance reports</p>
            {hasFeature('reports') ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/reports')}>
                Generate Report
              </button>
            ) : (
              <span className="feature-disabled">Not Available</span>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="activity-panel">
          <div className="panel-header">
            <h2>Recent Activity</h2>
            <button className="btn btn-ghost btn-sm">
              <i className="fas fa-refresh"></i>
              Refresh
            </button>
          </div>

          <div className="activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon" style={{ color: getStatusColor(activity.type === 'transaction' ? (activity.title.includes('Credit') ? 'success' : 'info') : activity.status) }}>
                    <i className={getActivityIcon(activity.type)}></i>
                  </div>
                  <div className="activity-content">
                    <h4>{activity.title}</h4>
                    <p>{activity.description}</p>
                    <span className="activity-time">
                      {new Date(activity.timestamp).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="activity-empty">
                <p>No recent activity found.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wallet Balance */}
      <div className="wallet-card">
        <div className="wallet-header">
          <h3><i className="fas fa-wallet"></i> Wallet Balance</h3>
          <div className="wallet-balance">
            <span className="currency">₹</span>
            <span className="amount">{user?.walletBalance?.toFixed(2) || '0.00'}</span>
          </div>
        </div>
        <div className="wallet-actions">
          <button className="btn btn-success btn-sm" onClick={() => navigate('/wallet')}>
            <i className="fas fa-plus"></i> Add Funds
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/wallet')}>
            <i className="fas fa-history"></i> Transaction History
          </button>
        </div>
      </div>
    </DashboardLayout >
  );
};

export default UserDashboard;